import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

export default function PhoneCamera() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraOn, setCameraOn] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);

  const API_BASE = "/api";

  useEffect(() => {
    if (!token) {
      setMessage("Lien caméra invalide.");
      return;
    }

    startCamera();

    return () => {
      stopCamera();
    };
  }, [token]);

  // Attach the stream after the <video> element is mounted.
  // This fixes the black camera preview on iPhone/Safari.
  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;

    if (!cameraOn || !video || !stream) return;

    let cancelled = false;

    const markReady = () => {
      if (!cancelled) {
        setCameraReady(true);
        setMessage("");
      }
    };

    const attachStream = async () => {
      try {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;

        video.onloadedmetadata = async () => {
          try {
            await video.play();
            markReady();
          } catch (error) {
            console.warn("VIDEO PLAY ERROR:", error);

            setTimeout(async () => {
              try {
                await video.play();
                markReady();
              } catch (retryError) {
                console.error("VIDEO PLAY RETRY ERROR:", retryError);
                if (!cancelled) {
                  setCameraReady(false);
                  setMessage(
                    "La caméra est autorisée mais n'arrive pas à démarrer. Appuyez sur « Ouvrir la caméra » ou utilisez la galerie.",
                  );
                }
              }
            }, 300);
          }
        };

        if (video.readyState >= 2) {
          try {
            await video.play();
            markReady();
          } catch (error) {
            console.warn("VIDEO PLAY ERROR:", error);
          }
        }
      } catch (error) {
        console.error("ATTACH CAMERA ERROR:", error);
        if (!cancelled) {
          setCameraReady(false);
          setMessage("Impossible d'afficher la caméra.");
        }
      }
    };

    attachStream();

    return () => {
      cancelled = true;
      video.onloadedmetadata = null;
    };
  }, [cameraOn]);

  const startCamera = async () => {
    try {
      setMessage("");
      setCameraReady(false);

      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraOn(false);
        setMessage(
          "La caméra directe n'est pas disponible. Utilisez le bouton ci-dessous.",
        );
        return;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1080 },
          height: { ideal: 1440 },
        },
        audio: false,
      });

      streamRef.current = stream;
      setCameraOn(true);
      setMessage("Initialisation de la caméra…");
    } catch (error) {
      console.error("GET USER MEDIA ERROR:", error);

      setCameraOn(false);
      setCameraReady(false);

      if (
        error?.name === "NotAllowedError" ||
        error?.name === "PermissionDeniedError"
      ) {
        setMessage(
          "Accès à la caméra refusé. Autorisez la caméra pour unitedmigrants.com dans les réglages du navigateur.",
        );
      } else if (error?.name === "NotFoundError") {
        setMessage(
          "Aucune caméra disponible. Vous pouvez utiliser la galerie.",
        );
      } else {
        setMessage(
          "Impossible d'ouvrir la caméra. Autorisez l'accès à la caméra ou utilisez la galerie.",
        );
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraOn(false);
    setCameraReady(false);
  };

  const uploadPhoto = async (blob, filename = "phone-photo.jpg") => {
    if (!token || !blob) return;

    setBusy(true);
    setMessage("Envoi de la photo vers l'ordinateur…");

    try {
      const formData = new FormData();
      formData.append("token", token);
      formData.append("photo", blob, filename);

      const response = await axios.post(
        `${API_BASE}/phoneCameraUpload.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Échec de l'envoi");
      }

      setMessage(
        "✅ Photo envoyée ! Vous pouvez maintenant attendre sur cet écran.",
      );
      stopCamera();
    } catch (error) {
      console.error(error);
      setMessage(
        error.response?.data?.message ||
          "❌ Impossible d'envoyer la photo. Réessayez.",
      );
    } finally {
      setBusy(false);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !cameraReady || video.readyState < 2) {
      setMessage("La caméra n'est pas encore prête.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setPreview(url);
          uploadPhoto(blob);
        }
      },
      "image/jpeg",
      0.92,
    );
  };

  const handleGallery = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const url = URL.createObjectURL(file);
    setPreview(url);
    uploadPhoto(file, file.name || "phone-photo.jpg");
  };

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <h1 style={styles.title}>📱 Caméra téléphone</h1>
          <p style={styles.error}>Lien caméra invalide ou incomplet.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.icon}>📷</div>
        <h1 style={styles.title}>Photo du membre</h1>
        <p style={styles.subtitle}>
          Prenez la photo avec votre téléphone. Elle sera automatiquement
          envoyée à l'ordinateur.
        </p>

        <div style={styles.cameraFrame}>
          {cameraOn ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                webkit-playsinline="true"
                style={{
                  ...styles.video,
                  visibility: cameraReady ? "visible" : "hidden",
                }}
              />
              {!cameraReady && (
                <div style={styles.cameraLoading}>
                  <div style={styles.loadingIcon}>📷</div>
                  <div>Initialisation de la caméra…</div>
                </div>
              )}
            </>
          ) : preview ? (
            <img src={preview} alt="Aperçu" style={styles.video} />
          ) : (
            <div style={styles.placeholder}>📷</div>
          )}
        </div>

        <canvas ref={canvasRef} style={{ display: "none" }} />

        {message && <p style={styles.message}>{message}</p>}

        {cameraOn && (
          <button
            type="button"
            onClick={capturePhoto}
            disabled={busy || !cameraReady}
            style={{
              ...styles.primaryButton,
              opacity: cameraReady && !busy ? 1 : 0.55,
            }}
          >
            {busy
              ? "Envoi…"
              : cameraReady
                ? "📸 Prendre la photo"
                : "⏳ Caméra en préparation…"}
          </button>
        )}

        {!cameraOn && !busy && (
          <button
            type="button"
            onClick={startCamera}
            style={styles.secondaryButton}
          >
            📷 Ouvrir la caméra
          </button>
        )}

        <label style={styles.galleryButton}>
          🖼️ Choisir une photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleGallery}
            style={{ display: "none" }}
            disabled={busy}
          />
        </label>

        <p style={styles.footer}>
          Vous pouvez fermer cette page après l'envoi.
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "#fff",
    borderRadius: 18,
    padding: 24,
    boxSizing: "border-box",
    textAlign: "center",
    boxShadow: "0 8px 30px rgba(0,0,0,.08)",
  },
  icon: { fontSize: 42 },
  title: { margin: "8px 0", fontSize: 26 },
  subtitle: { color: "#666", lineHeight: 1.5 },
  cameraFrame: {
    position: "relative",
    width: "100%",
    aspectRatio: "3 / 4",
    background: "#111",
    borderRadius: 14,
    overflow: "hidden",
    margin: "20px 0",
  },
  video: { width: "100%", height: "100%", objectFit: "cover" },
  placeholder: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#777",
    fontSize: 60,
  },
  cameraLoading: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    color: "#fff",
    background: "#111",
    fontSize: 16,
    zIndex: 2,
  },
  loadingIcon: {
    fontSize: 48,
  },
  message: { minHeight: 24, color: "#444", lineHeight: 1.4 },
  primaryButton: {
    width: "100%",
    border: 0,
    borderRadius: 10,
    padding: "14px 18px",
    background: "#ff6600",
    color: "white",
    fontSize: 17,
    fontWeight: 700,
    cursor: "pointer",
    marginBottom: 10,
  },
  secondaryButton: {
    width: "100%",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "14px 18px",
    background: "white",
    fontSize: 17,
    cursor: "pointer",
    marginBottom: 10,
  },
  galleryButton: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "14px 18px",
    cursor: "pointer",
    fontSize: 16,
  },
  footer: { color: "#999", fontSize: 13, marginTop: 20 },
  error: { color: "#c00" },
};
